import { useState, useEffect } from "react";
import { Sidebar } from "@/components/Sidebar";
import SupportChat from "@/components/SupportChat";
import NotificationBell from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { Wallet, UserCog, ShieldAlert, X, Menu, Megaphone, LogOut } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export function AppLayout({ children, variant = "user", requireAdmin = false }) {
  const { user, loading, impersonation, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // Close mobile drawer when route changes
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const handleTopbarLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (requireAdmin && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="flex min-h-screen">
      {/* Desktop sidebar */}
      <div className="hidden md:flex">
        <Sidebar variant={variant} />
      </div>

      {/* Mobile drawer overlay + sidebar */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in" onClick={()=>setDrawerOpen(false)}/>
          <div className="relative z-10 animate-in slide-in-from-left duration-200 h-full max-h-[100dvh]">
            <Sidebar variant={variant} />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0">
        {/* Impersonation banner */}
        {impersonation && (
          <div data-testid="impersonation-banner" className="bg-nb-cyan text-black text-xs font-semibold px-4 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <ShieldAlert size={14}/>
              <span>Admin session — viewing <span className="font-mono">{user.email}</span> as <span className="font-mono">{impersonation.admin_email}</span>.</span>
            </div>
            <button data-testid="impersonation-exit" onClick={()=>{ logout(); window.close(); }} className="rounded-full bg-black text-nb-cyan px-3 py-0.5 flex items-center gap-1 hover:opacity-80"><X size={11}/> Exit</button>
          </div>
        )}

        {/* Top bar */}
        <div className="sticky top-0 z-30 backdrop-blur-md bg-[#070B14]/80 border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 md:px-10 py-3 flex items-center justify-between gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={()=>setDrawerOpen(true)}
              data-testid="mobile-menu-btn"
              className="md:hidden p-2 rounded-lg border border-white/10 bg-[#0F1626] text-slate-200 hover:border-nb-cyan/40"
              aria-label="Open menu"
            >
              <Menu size={18}/>
            </button>
            <div className="text-xs text-slate-500 uppercase tracking-widest hidden md:block">{variant === "admin" ? "Admin Console" : "Investor Panel"}</div>
            <div className="flex items-center gap-2 md:gap-3 ml-auto">
              <div data-testid="topbar-wallet" className="flex items-center gap-2 rounded-full border border-[#8AE234]/30 bg-[#8AE234]/5 px-3 py-1.5">
                <Wallet size={14} className="text-[#8AE234]"/>
                <div className="text-[10px] text-slate-400 uppercase tracking-widest hidden sm:inline">Wallet</div>
                <div className="font-mono text-[#8AE234] font-bold text-sm">${(user?.balance || 0).toFixed(2)}</div>
              </div>
              {variant === "user" && (
                <div data-testid="topbar-account-status" className={`hidden sm:flex items-center gap-1.5 rounded-full px-3 py-1.5 border ${user?.account_status === "active" ? "border-[#8AE234]/30 bg-[#8AE234]/5 text-[#8AE234]" : "border-amber-500/30 bg-amber-500/5 text-amber-300"}`}>
                  <div className={`h-2 w-2 rounded-full ${user?.account_status === "active" ? "bg-[#8AE234]" : "bg-amber-400"}`}/>
                  <span className="text-[10px] uppercase tracking-widest font-bold">{user?.account_status === "active" ? "Active" : "Inactive"}</span>
                </div>
              )}
              {variant === "user" && <NotificationBell/>}
              <Link to={variant === "admin" ? "/admin" : "/profile"} data-testid="topbar-profile" className="flex items-center gap-2 rounded-full border border-white/10 bg-[#0F1626] px-2.5 py-1.5 hover:border-nb-cyan/40">
                <div className="h-6 w-6 rounded-full bg-nb-cyan/20 flex items-center justify-center text-nb-cyan text-xs font-bold">{user.name?.[0]?.toUpperCase() || "U"}</div>
                <div className="text-xs text-white hidden sm:inline">{user.name}</div>
                <UserCog size={14} className="text-slate-400"/>
              </Link>
              <button
                data-testid="topbar-signout"
                onClick={handleTopbarLogout}
                title="Sign out"
                aria-label="Sign out"
                className="flex items-center gap-1.5 rounded-full border border-red-500/30 bg-red-500/5 hover:bg-red-500/10 hover:border-red-500/50 text-red-400 px-2.5 py-1.5 transition-colors"
              >
                <LogOut size={14}/>
                <span className="text-xs font-semibold hidden sm:inline">Sign out</span>
              </button>
            </div>
          </div>
        </div>

        {/* Announcement popup only on user side */}
        {variant === "user" && <AnnouncementPopup/>}

        <div className="px-4 md:px-10 py-6 md:py-8 max-w-7xl mx-auto overflow-x-hidden">
          {children}
        </div>
        {/* AI Support chat bubble — user side only */}
        {variant === "user" && <SupportChat/>}
      </main>
    </div>
  );
}

// Risk warning removed per platform requirement (item 17).
// Kept as an exported no-op so existing imports don't break during transition.
export function RiskBanner() { return null; }

// Announcement popup — auto-closes after 10 seconds, or on manual dismiss.
// Only shows unseen announcements (dismissed IDs stored in localStorage).
function AnnouncementPopup() {
  const [ann, setAnn] = useState(null);
  const [seconds, setSeconds] = useState(10);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/announcements");
        const items = data.announcements || [];
        if (!items.length || !mounted) return;
        const seen = JSON.parse(localStorage.getItem("nb_ann_seen") || "[]");
        const next = items.find(a => !seen.includes(a.id));
        if (next && mounted) setAnn(next);
      } catch (_) { /* ignore fetch failure */ }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!ann) return;
    setSeconds(10);
    const iv = setInterval(() => {
      setSeconds(s => {
        if (s <= 1) { clearInterval(iv); dismiss(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [ann?.id]);

  const dismiss = () => {
    if (!ann) return;
    const seen = JSON.parse(localStorage.getItem("nb_ann_seen") || "[]");
    if (!seen.includes(ann.id)) {
      seen.push(ann.id);
      localStorage.setItem("nb_ann_seen", JSON.stringify(seen.slice(-50)));
    }
    setAnn(null);
  };

  if (!ann) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4 pointer-events-none" data-testid="announcement-popup">
      <div className="pointer-events-auto max-w-xl w-full rounded-2xl border border-nb-cyan/40 bg-gradient-to-br from-[#0F1626] via-[#0A1626] to-[#070B14] p-5 shadow-2xl shadow-nb-cyan/20 animate-in fade-in slide-in-from-top-4 duration-300">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-nb-cyan/15 p-2 shrink-0"><Megaphone size={18} className="text-nb-cyan"/></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="font-heading text-white text-lg">{ann.title || "Announcement"}</div>
              <button data-testid="announcement-close" onClick={dismiss} className="text-slate-400 hover:text-white shrink-0"><X size={16}/></button>
            </div>
            <div className="text-slate-300 text-sm mt-1 whitespace-pre-wrap break-words">{ann.body}</div>
            <div className="mt-3 flex items-center justify-between text-[10px] text-slate-500 uppercase tracking-widest">
              <span>{new Date(ann.created_at).toLocaleString()}</span>
              <span>Auto-closes in <span className="text-nb-cyan font-mono">{seconds}s</span></span>
            </div>
            <div className="mt-2 h-0.5 bg-white/5 rounded overflow-hidden">
              <div className="h-full bg-nb-cyan transition-all duration-1000" style={{width: `${(seconds/10)*100}%`}}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
